// Stage 33: Bulletin Canvas — polling hook for the panels list.
//
// Polls every POLL_MS while the tab is visible. When the tab is hidden the
// timer keeps ticking but the fetch is skipped, so refocus produces an
// immediate refresh without churn during background.

import { useEffect, useState } from 'react';
import { listPanels } from './bulletinClient';
import type { BulletinPanelDTO } from './types';

const POLL_MS = 30_000;

export function useBulletinPolling(): {
  panels: BulletinPanelDTO[];
  setPanels: React.Dispatch<React.SetStateAction<BulletinPanelDTO[]>>;
  error: string | null;
  refresh: () => Promise<void>;
} {
  const [panels, setPanels] = useState<BulletinPanelDTO[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async (): Promise<void> => {
      if (!alive) return;
      if (
        typeof document !== 'undefined' &&
        document.visibilityState === 'hidden'
      ) {
        timer = setTimeout(tick, POLL_MS);
        return;
      }
      try {
        const fresh = await listPanels();
        if (!alive) return;
        setPanels(fresh);
        setError(null);
      } catch (err) {
        if (!alive) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (alive) timer = setTimeout(tick, POLL_MS);
      }
    };
    void tick();

    return () => {
      alive = false;
      if (timer !== null) clearTimeout(timer);
    };
  }, []);

  const refresh = async (): Promise<void> => {
    try {
      const fresh = await listPanels();
      setPanels(fresh);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return { panels, setPanels, error, refresh };
}
